import { useState, useRef, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { X, Trash2, MessageSquare, Send, ExternalLink, Check, ShoppingCart, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { slideInRight } from '@/lib/motion'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal, useBodyScrollLock } from '@/components/ui/Modal'
import { Skeleton } from '@/components/ui/Skeleton'
import { BookingStatusBadge } from './BookingStatusBadge'
import { BookingForm } from './BookingForm'
import { BookingPreOrderModal } from './BookingPreOrderModal'
import { useBooking } from '@/lib/queries/useBookings'
import { useBookingActions } from '@/lib/hooks/useBookingActions'
import { useCreateComment } from '@/lib/queries/useComments'
import { bookingTableNames, hasBookingTables, hasBrokenBookingTableRefs, hasUsableBookingTable } from '@/lib/booking/core'
import { useBookingPreOrderItems, useRemovePreOrderItem } from '@/lib/booking/preOrderAdapter'
import { useActivePOSOrderForBooking, useEnsurePOSOrderForBooking } from '@/lib/booking/posBridge'
import { BookingStatusTimeline } from './BookingStatusTimeline'
import { formatDate, formatRelative } from '@/lib/utils/date'
import { formatPOSMoney, usePOSCurrency } from '@/lib/pos/currency'

interface BookingDetailPanelProps {
  documentId: string | null
  onClose: () => void
}

export function BookingDetailPanel({ documentId, onClose }: BookingDetailPanelProps) {
  useBodyScrollLock(!!documentId)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const currency = usePOSCurrency()
  const { data: resp, isLoading } = useBooking(documentId ?? '')
  const bookingActions = useBookingActions()
  const createComment = useCreateComment()
  const [savingNote, setSavingNote] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const [showEdit, setShowEdit] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [showPreOrder, setShowPreOrder] = useState(false)

  const { data: preOrderResp } = useBookingPreOrderItems(documentId)
  const preOrderItems: any[] = (preOrderResp as any)?.data ?? []
  const removePreOrderItem = useRemovePreOrderItem()

  const { data: posOrderResp } = useActivePOSOrderForBooking(documentId)
  const activePOSOrder: any = (posOrderResp as any)?.data?.[0] ?? null
  const ensurePOSOrder = useEnsurePOSOrderForBooking()
  const [note, setNote] = useState('')
  const [noteSaved, setNoteSaved] = useState(false)
  const [commentText, setCommentText] = useState('')
  const noteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const booking = resp?.data

  // Sync note state when booking loads or documentId changes
  useEffect(() => {
    if (booking) setNote(booking.note ?? '')
    setNoteSaved(false)
  }, [booking?.documentId])

  const saveNote = () => {
    if (!booking) return
    if (noteTimeoutRef.current) clearTimeout(noteTimeoutRef.current)
    setSavingNote(true)
    bookingActions.update(booking.documentId, { note }).then(() => {
      setNoteSaved(true)
      noteTimeoutRef.current = setTimeout(() => setNoteSaved(false), 2000)
    }).finally(() => setSavingNote(false))
  }

  const submitComment = () => {
    if (!booking || !commentText.trim()) return
    createComment.mutate(
      { bookingDocumentId: booking.documentId, text: commentText.trim() },
      { onSuccess: () => setCommentText('') },
    )
  }

  return (
    <>
      <AnimatePresence>
        {documentId && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[900] bg-black/60"
              onClick={onClose}
            />
            <motion.div
              {...slideInRight}
              className="fixed inset-0 z-[901] flex flex-col bg-[var(--color-bg-surface)] shadow-2xl sm:inset-y-0 sm:left-auto sm:right-0 sm:w-full sm:max-w-md sm:border-l sm:border-[var(--color-border-subtle)]"
            >
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--color-border-subtle)] px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top))]">
                {booking ? (
                  <div className="flex items-center gap-3 min-w-0">
                    <div>
                      <h3 className="font-display font-semibold text-[var(--color-text-primary)] truncate">
                        {booking.guest?.name ?? 'Walk-in'}
                      </h3>
                      <p className="text-xs text-[var(--color-text-muted)]">
                        Created {formatRelative(booking.createdAt)}
                      </p>
                    </div>
                    <BookingStatusBadge status={booking.status} />
                  </div>
                ) : (
                  <div className="h-5 w-40 bg-[var(--color-bg-elevated)] rounded animate-pulse" />
                )}
                <div className="flex items-center gap-1 shrink-0">
                  {booking && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => navigate(`/bookings/${booking.documentId}`)}
                      title={t('bookings.detail.openFullPage')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                  <button
                    onClick={onClose}
                    aria-label="Close booking details"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 touch-pan-y space-y-4 overflow-y-auto overscroll-contain p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
                {isLoading || !booking ? (
                  <div className="space-y-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} variant="rectangular" height={80} />
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Booking Details */}
                    <section className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-4">
                      <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-3">
                        {t('bookings.detail.bookingDetails')}
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                        <div>
                          <p className="text-[var(--color-text-muted)] text-xs mb-0.5">{t('bookings.detail.date')}</p>
                          <p className="font-medium text-[var(--color-text-primary)]">{formatDate(booking.date)}</p>
                        </div>
                        <div>
                          <p className="text-[var(--color-text-muted)] text-xs mb-0.5">{t('bookings.detail.time')}</p>
                          <p className="font-medium text-[var(--color-text-primary)]">{booking.time}</p>
                        </div>
                        <div>
                          <p className="text-[var(--color-text-muted)] text-xs mb-0.5">{t('bookings.detail.partySize')}</p>
                          <p className="font-medium text-[var(--color-text-primary)]">{t('bookings.detail.guests', { count: booking.people })}</p>
                        </div>
                        <div>
                          <p className="text-[var(--color-text-muted)] text-xs mb-0.5">{t('bookings.detail.duration')}</p>
                          <p className="font-medium text-[var(--color-text-primary)]">{t('bookings.detail.durationValue', { count: booking.duration })}</p>
                        </div>
                        {booking.source && (
                          <div>
                            <p className="text-[var(--color-text-muted)] text-xs mb-0.5">{t('bookings.detail.source')}</p>
                            {booking.source === 'she_answers' ? (
                              <span className="inline-block rounded-full bg-purple-600 px-2 py-0.5 text-[10px] font-medium text-white">
                                SheAnswers
                              </span>
                            ) : (
                              <p className="font-medium text-[var(--color-text-primary)] capitalize">{booking.source}</p>
                            )}
                          </div>
                        )}
                        {hasBookingTables(booking) && (
                          <div>
                            <p className="text-[var(--color-text-muted)] text-xs mb-0.5">{t('bookings.detail.tables')}</p>
                            <p className="font-medium text-[var(--color-text-primary)]">
                              {bookingTableNames(booking).join(', ')}
                            </p>
                          </div>
                        )}
                        {booking.status === 'left' && (
                          <>
                            <div>
                              <p className="text-[var(--color-text-muted)] text-xs mb-0.5">{t('bookings.detail.amountSpent')}</p>
                              <p className="font-medium text-[var(--color-text-primary)]">
                                {formatPOSMoney(Number(booking.metadata?.spend ?? 0), currency)}
                              </p>
                            </div>
                            <div>
                              <p className="text-[var(--color-text-muted)] text-xs mb-0.5">{t('bookings.detail.tipLeft')}</p>
                              <p className="font-medium text-[var(--color-text-primary)]">
                                {formatPOSMoney(Number(booking.metadata?.tip ?? 0), currency)}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </section>

                    {/* Guest Info */}
                    <section className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-4">
                      <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-3">
                        {t('bookings.detail.guest')}
                      </p>
                      <div className="space-y-2 text-sm">
                        {booking.guest?.email && (
                          <p className="text-[var(--color-text-primary)]">
                            <span className="text-[var(--color-text-muted)]">Email: </span>
                            {booking.guest.email}
                          </p>
                        )}
                        {booking.guest?.phone && (
                          <p className="text-[var(--color-text-primary)]">
                            <span className="text-[var(--color-text-muted)]">Phone: </span>
                            {booking.guest.phone}
                          </p>
                        )}
                        {!booking.guest?.email && !booking.guest?.phone && (
                          <p className="text-[var(--color-text-muted)]">{t('bookings.detail.noContactInfo')}</p>
                        )}
                      </div>
                    </section>

                    {/* Pre-order */}
                    <section className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide flex items-center gap-1.5">
                          <ShoppingCart className="h-3.5 w-3.5" /> Pre-order
                        </p>
                        <button
                          onClick={() => setShowPreOrder(true)}
                          className="flex items-center gap-1 text-xs text-[var(--color-accent-amber)] hover:underline"
                        >
                          <Plus className="h-3 w-3" /> Add item
                        </button>
                      </div>
                      {preOrderItems.length === 0 ? (
                        <p className="text-xs text-[var(--color-text-muted)] italic">
                          No pre-order. Guest can order on arrival.
                        </p>
                      ) : (
                        <ul className="space-y-1">
                          {preOrderItems.map((item: any) => (
                            <li key={item.documentId} className="flex items-center justify-between gap-2 text-sm">
                              <span className="text-[var(--color-text-primary)]">
                                {item.weightGrams != null ? `${item.weightGrams % 1000 === 0 ? `${item.weightGrams / 1000} kg` : `${item.weightGrams} g`}` : `${item.quantity}×`} {item.menuItem?.name}
                                {item.variant ? ` — ${item.variant.name}` : ''}
                              </span>
                              <button
                                onClick={() => removePreOrderItem.mutate({ documentId: item.documentId, bookingDocumentId: booking.documentId })}
                                className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-accent-red)] transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>

                    {/* POS Order — shown when booking is arrived/seated */}
                    {(booking.status === 'arrived' || booking.status === 'seated') && (
                      <section className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-4">
                        <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-3">
                          POS Order
                        </p>
                        {activePOSOrder ? (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="h-2 w-2 rounded-full bg-green-400 shrink-0" />
                            <span className="text-green-400 font-medium">Active</span>
                            <span className="text-[var(--color-text-muted)]">·</span>
                            <span className="text-[var(--color-text-secondary)]">
                              {activePOSOrder.table?.name ?? 'table'}
                            </span>
                            {(activePOSOrder.orderItems?.length ?? 0) > 0 && (
                              <>
                                <span className="text-[var(--color-text-muted)]">·</span>
                                <span className="text-[var(--color-text-muted)] text-xs">
                                  {activePOSOrder.orderItems.length} item{activePOSOrder.orderItems.length !== 1 ? 's' : ''}
                                </span>
                              </>
                            )}
                          </div>
                        ) : (() => {
                          const brokenRefs = hasBrokenBookingTableRefs(booking)
                          const hasNoTable = !hasUsableBookingTable(booking)
                          if (brokenRefs) {
                            return (
                              <p className="text-xs text-amber-400">
                                ⚠ Table reference broken — re-assign the table in <strong>Strapi Admin → Bookings</strong> then click Create POS Order.
                              </p>
                            )
                          }
                          return (
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-[var(--color-text-muted)]">
                                {hasNoTable ? 'No table assigned' : 'No active POS order'}
                              </span>
                              {!hasNoTable && (
                                <button
                                  onClick={() => ensurePOSOrder.mutate({ bookingDocumentId: booking.documentId })}
                                  disabled={ensurePOSOrder.isPending}
                                  className="text-xs px-2.5 py-1 rounded-[var(--radius-sm)] bg-[var(--color-bg-base)] border border-[var(--color-border-subtle)] hover:border-[var(--color-border)] text-[var(--color-text-secondary)] transition-colors disabled:opacity-50"
                                >
                                  {ensurePOSOrder.isPending ? 'Creating…' : 'Create POS Order'}
                                </button>
                              )}
                            </div>
                          )
                        })()}
                      </section>
                    )}

                    {/* Guest Note */}
                    {booking.comment && (
                      <section className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-4">
                        <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-2">
                          {t('bookings.detail.guestNote')}
                        </p>
                        <p className="text-sm text-[var(--color-text-secondary)]">{booking.comment}</p>
                      </section>
                    )}

                    {/* Internal Note */}
                    <section className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">
                          {t('bookings.detail.internalNote')}
                        </p>
                        {noteSaved ? (
                          <span className="flex items-center gap-1 text-xs text-green-500">
                            <Check className="h-3 w-3" /> {t('bookings.detail.saved')}
                          </span>
                        ) : (
                          <button
                            onClick={saveNote}
                            disabled={savingNote || note === (booking.note ?? '')}
                            className="text-xs text-[var(--color-accent-amber)] hover:underline disabled:opacity-40 disabled:no-underline"
                          >
                            {savingNote ? t('bookings.detail.saving') : t('bookings.detail.save')}
                          </button>
                        )}
                      </div>
                      <textarea
                        value={note}
                        onChange={(e) => { setNote(e.target.value); setNoteSaved(false) }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveNote()
                        }}
                        placeholder={t('bookings.detail.addInternalNote')}
                        className="w-full h-20 bg-[var(--color-bg-base)] rounded-[var(--radius-sm)] p-3 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50 resize-none"
                      />
                      <p className="text-[0.65rem] text-[var(--color-text-muted)] mt-1">{t('bookings.detail.cmdEnterToSave')}</p>
                    </section>

                    {/* Comments */}
                    <section className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-4">
                      <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <MessageSquare className="h-3.5 w-3.5" /> {t('bookings.detail.comments')}
                      </p>
                      <div className="space-y-2 mb-3">
                        {(booking.comments ?? []).length === 0 ? (
                          <p className="text-xs text-[var(--color-text-muted)]">{t('bookings.detail.noComments')}</p>
                        ) : (
                          (booking.comments ?? []).map((c) => (
                            <div
                              key={c.id}
                              className={`p-3 rounded-[var(--radius-sm)] text-sm ${
                                c.role === 'restaurant'
                                  ? 'bg-[var(--color-bg-base)] ml-4'
                                  : 'bg-[var(--color-accent-amber-dim)] mr-4'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant={c.role === 'restaurant' ? 'default' : 'amber'}>{c.role}</Badge>
                                <span className="text-xs text-[var(--color-text-muted)]">{formatRelative(c.createdAt)}</span>
                              </div>
                              <p className="text-[var(--color-text-primary)]">{c.text}</p>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') submitComment() }}
                          placeholder={t('bookings.detail.addComment')}
                          className="flex-1 h-9 px-3 rounded-[var(--radius-md)] bg-[var(--color-bg-base)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50"
                        />
                        <Button
                          size="icon-sm"
                          onClick={submitComment}
                          loading={createComment.isPending}
                          disabled={!commentText.trim()}
                        >
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </section>

                    {/* Status Timeline */}
                    <section className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-4">
                      <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-4">
                        Status
                      </p>
                      <BookingStatusTimeline
                        documentId={booking.documentId}
                        status={booking.status}
                        metadata={booking.metadata}
                      />
                    </section>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-[var(--color-border-subtle)]">
                      <Button variant="secondary" size="sm" className="flex-1" onClick={() => setShowEdit(true)}>
                        {t('bookings.editBooking')}
                      </Button>
                      <Button variant="danger" size="icon-sm" onClick={() => setShowDelete(true)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {booking && (
        <>
          <BookingPreOrderModal
            open={showPreOrder}
            onClose={() => setShowPreOrder(false)}
            bookingDocumentId={booking.documentId}
          />
          <BookingForm open={showEdit} onClose={() => setShowEdit(false)} booking={booking} />
          <Modal open={showDelete} onClose={() => setShowDelete(false)} title={t('bookings.detail.deleteConfirmTitle')} size="sm">
            <p className="text-sm text-[var(--color-text-secondary)] mb-4">
              {t('bookings.detail.deleteConfirmDesc', { name: booking.guest?.name ?? 'Walk-in' })}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setShowDelete(false)}>{t('common.cancel')}</Button>
              <Button
                variant="danger"
                size="sm"
                loading={deleting}
                onClick={() => { setDeleting(true); bookingActions.remove(booking.documentId).then(onClose).finally(() => setDeleting(false)) }}
              >
                {t('common.delete')}
              </Button>
            </div>
          </Modal>
        </>
      )}
    </>
  )
}
